<template>
  <router-link
    :to="`/investigations/${investigation.id}`"
    class="investigation-list-item"
    :class="{
      'active': isActive,
      [`status-${investigation.status_code}`]: true
    }"
    :aria-label="`Investigation ${displayId}, status ${investigation.status_code}`"
  >
    <div class="investigation-content">
      <div class="investigation-header">
        <span class="investigation-id">{{ displayId }}</span>
        <span class="status-badge" :class="`status-${investigation.status_code}`">
          {{ investigation.status_code }}
        </span>
      </div>
      <div class="investigation-meta">
        <span class="timestamp" :title="absoluteTimestamp">
          {{ relativeTimestamp }}
        </span>
        <span v-if="investigation.duration" class="duration">
          {{ formattedDuration }}
        </span>
      </div>
    </div>
  </router-link>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';

const props = defineProps({
  investigation: {
    type: Object,
    required: true,
    validator: (value) => {
      return (
        ('session_id' in value || 'fingerprint' in value) &&
        'status' in value &&
        'timestamp' in value
      );
    }
  }
});

const route = useRoute();

// Check if this investigation is currently active
const isActive = computed(() => {
  const currentId = route.params.id;
  return (
    currentId === props.investigation.session_id ||
    currentId === props.investigation.fingerprint
  );
});

// Display ID (truncated for readability)
const displayId = computed(() => {
  const id = props.investigation.session_id || props.investigation.fingerprint;
  return id.length > 12 ? `${id.substring(0, 12)}...` : id;
});

// Formatted duration
const formattedDuration = computed(() => {
  if (!props.investigation.duration) return '';
  const duration = props.investigation.duration;
  return duration < 60 ? `${duration.toFixed(1)}s` : `${(duration / 60).toFixed(1)}m`;
});

// Absolute timestamp for title attribute
const absoluteTimestamp = computed(() => {
  const date = new Date(props.investigation.timestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
});

// Relative timestamp (e.g., "2 minutes ago")
const relativeTimestamp = computed(() => {
  const date = new Date(props.investigation.timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
});
</script>

<style scoped>
.investigation-list-item {
  display: block;
  padding: 0.75rem;
  border-radius: 0.375rem;
  text-decoration: none;
  color: #f3f4f6 !important;
  background: rgba(255, 255, 255, 0.03);
  transition: background-color 0.2s ease, transform 0.1s ease;
  cursor: pointer;
  min-height: 48px;
  border-left: 3px solid transparent;
}

.investigation-list-item:hover {
  background-color: rgba(255, 255, 255, 0.05);
  transform: translateX(2px);
}

.investigation-list-item:focus {
  outline: 2px solid var(--bs-primary);
  outline-offset: 2px;
}

.investigation-list-item.active {
  background-color: rgba(13, 110, 253, 0.1);
  border-left-color: var(--bs-primary);
}

/* Status-specific border colors */
.investigation-list-item.status-success {
  border-left-color: var(--bs-success);
}

.investigation-list-item.status-error {
  border-left-color: var(--bs-danger);
}

.investigation-list-item.status-active {
  border-left-color: var(--bs-info);
}

.investigation-list-item.status-deduped {
  border-left-color: var(--bs-warning);
}

.investigation-content {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.investigation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}

.investigation-id {
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.875rem;
  font-weight: 500;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #e5e7eb !important;
}

.status-badge {
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
}

.status-badge.status-success {
  background-color: var(--bs-success);
  color: white;
}

.status-badge.status-error {
  background-color: var(--bs-danger);
  color: white;
}

.status-badge.status-active {
  background-color: var(--bs-info);
  color: white;
}

.status-badge.status-deduped {
  background-color: var(--bs-warning);
  color: var(--bs-dark);
}

.investigation-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
  color: var(--bs-secondary);
}

.timestamp {
  flex: 1;
}

.duration {
  font-weight: 500;
  color: var(--bs-light);
}

/* Touch-friendly sizing on mobile */
@media (max-width: 768px) {
  .investigation-list-item {
    min-height: 56px;
    padding: 1rem 0.75rem;
  }
}
</style>
